import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Message } from '../../hooks/useChat';
import { MessageMedia } from '../../hooks/chat/types';
import { isAfter } from '../../hooks/chat/mappers';
import { dayKey, formatAgo, formatDateTime, formatDay, formatTime } from './format';
import { MessageActionChips } from './MessageActions';

interface Props {
  /** Every message of the session, oldest first; undefined while loading. */
  messages: Message[] | undefined;
  /** The ones to show (a search narrows them). */
  visibleMessages: Message[];
  /** When the rider last read the conversation: marks the replies they have seen. */
  riderLastRead?: string | null;
  emptyText: string;
  /** Rendered after the last message (the closed banner). */
  footer?: ReactNode;
  /** Scrolling resets when this changes (the session id). */
  threadKey: string;
  /** The search narrowing `visibleMessages`: a new one is not "new messages". */
  filterKey?: string;
  /** The signed-in admin's ant id: their own sends scroll into view. */
  myAdminId?: string | null;
}

/** Closer than this to the bottom counts as "reading the latest". */
const NEAR_BOTTOM_PX = 80;

/** What the "N new messages" pill counts: what the rider or the assistant wrote. */
const countsAsNew = (m: Message) =>
  m.sender_type === 'user' || m.sender_type === 'assistant';

/** A reply or note the signed-in admin just sent. */
const isOwn = (m: Message | undefined, myAdminId: string | null | undefined) =>
  Boolean(
    m &&
      myAdminId &&
      (m.sender_type === 'admin' || m.sender_type === 'note') &&
      m.sender_id === myAdminId
  );

function MessageTime({ iso }: { iso: string }) {
  const ago = formatAgo(iso);
  const title = `${formatDateTime(iso)} (${ago === 'just now' ? ago : `${ago} ago`})`;
  return (
    <Tooltip title={title}>
      <Typography variant="caption" sx={{ opacity: 0.7, fontSize: 11 }}>
        {formatTime(iso)}
      </Typography>
    </Tooltip>
  );
}

/** The largest a photo or video shows in a bubble. */
const MEDIA_MAX_WIDTH = 260;
const MEDIA_MAX_HEIGHT = 220;

/**
 * The size a photo or video of this frame shows at, within the bubble's
 * limits, so it holds its space before it loads; null when ant does not know
 * the frame.
 */
function fitted(media: MessageMedia): { width: number; height: number } | null {
  if (!media.width || !media.height) return null;
  const scale = Math.min(
    MEDIA_MAX_WIDTH / media.width,
    MEDIA_MAX_HEIGHT / media.height,
    1
  );
  return {
    width: Math.round(media.width * scale),
    height: Math.round(media.height * scale),
  };
}

/**
 * A video from the bucket, played in place. ant signs a new URL every 50
 * minutes or so; one not started takes it at once, one under way keeps the
 * URL it is playing until that one fails (it expires an hour after ant
 * signed it), and then picks up where it was on the new one: at once when
 * the new URL is already here, or as soon as one arrives.
 */
function ChatVideo({ media, onLoad }: { media: MessageMedia; onLoad?: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState(media.url);
  const latestRef = useRef(media.url);
  latestRef.current = media.url;
  const resumeAtRef = useRef<number | null>(null);
  /** The URL playing failed with no newer one to take yet. */
  const erroredRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (media.url === src) return;
    if (!el || (el.paused && !el.currentTime)) {
      setSrc(media.url);
      return;
    }
    if (erroredRef.current || el.error) {
      if (resumeAtRef.current === null) resumeAtRef.current = el.currentTime || null;
      erroredRef.current = false;
      setSrc(media.url);
    }
  }, [media.url, src]);

  const onError = () => {
    const latest = latestRef.current;
    // Where it was; a retry failing at 0 keeps the point from before it.
    const at = ref.current?.currentTime || null;
    if (at !== null) resumeAtRef.current = at;
    if (!latest || latest === src) {
      erroredRef.current = true;
      return;
    }
    setSrc(latest);
  };

  const onLoadedMetadata = () => {
    const el = ref.current;
    erroredRef.current = false;
    if (el && resumeAtRef.current) {
      el.currentTime = resumeAtRef.current;
      resumeAtRef.current = null;
    }
    onLoad?.();
  };

  const size = fitted(media);
  return (
    <Box
      component="video"
      ref={ref}
      src={src ?? undefined}
      poster={media.poster_url ?? undefined}
      controls
      playsInline
      preload="metadata"
      onError={onError}
      onLoadedMetadata={onLoadedMetadata}
      sx={{
        display: 'block',
        borderRadius: 1.5,
        bgcolor: 'common.black',
        ...(size ?? { width: MEDIA_MAX_WIDTH, maxHeight: MEDIA_MAX_HEIGHT }),
      }}
      data-testid="message-video"
    />
  );
}

/** A photo or video from the bucket, or what it was while ant has media off. */
function BucketMedia({
  media,
  alt,
  onOpen,
  onLoad,
}: {
  media: MessageMedia;
  alt: string;
  onOpen: (url: string) => void;
  onLoad?: () => void;
}) {
  const video = media.kind === 'video';
  if (!media.url) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ opacity: 0.8 }}
        data-testid="media-unavailable"
      >
        {video ? (
          <VideocamOutlinedIcon fontSize="small" />
        ) : (
          <PhotoOutlinedIcon fontSize="small" />
        )}
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          {video ? 'Video' : 'Photo'} unavailable right now
        </Typography>
      </Stack>
    );
  }
  if (video) return <ChatVideo media={media} onLoad={onLoad} />;
  const { url } = media;
  const size = fitted(media);
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      onClick={() => onOpen(url)}
      onLoad={onLoad}
      sx={{
        display: 'block',
        borderRadius: 1.5,
        cursor: 'zoom-in',
        objectFit: 'cover',
        ...(size ?? { maxWidth: MEDIA_MAX_WIDTH, maxHeight: MEDIA_MAX_HEIGHT }),
      }}
      data-testid="message-image"
    />
  );
}

/** What a message carries besides text: a bucket photo or video, or a legacy photo. */
function Attachment({
  message,
  onOpen,
  onLoad,
}: {
  message: Message;
  onOpen: (url: string) => void;
  onLoad?: () => void;
}) {
  if (message.media) {
    return (
      <BucketMedia
        media={message.media}
        alt={message.content || (message.media.kind === 'video' ? 'Video' : 'Photo')}
        onOpen={onOpen}
        onLoad={onLoad}
      />
    );
  }
  if (message.image_url) {
    return (
      <Box
        component="img"
        src={message.image_url}
        alt={message.content || 'Photo'}
        onClick={() => onOpen(message.image_url as string)}
        onLoad={onLoad}
        sx={{
          display: 'block',
          maxWidth: 260,
          maxHeight: 220,
          borderRadius: 1.5,
          cursor: 'zoom-in',
          objectFit: 'cover',
        }}
        data-testid="message-image"
      />
    );
  }
  if (message.image_pending) {
    return (
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ opacity: 0.8 }}>
        <PhotoOutlinedIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          Photo pending approval
        </Typography>
      </Stack>
    );
  }
  return null;
}

/**
 * The conversation: rider on the left, agents on the right, internal notes
 * as amber cards, the assistant and system messages labelled, a separator
 * per day. It follows new messages while the agent is at the bottom and
 * offers a "New messages" button when they have scrolled up.
 */
export function MessageThread({
  messages,
  visibleMessages,
  riderLastRead,
  emptyText,
  footer,
  threadKey,
  filterKey = '',
  myAdminId = null,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const lastCountRef = useRef(0);
  const lastFilterRef = useRef(filterKey);
  const visibleRef = useRef(visibleMessages);
  visibleRef.current = visibleMessages;
  const myAdminIdRef = useRef(myAdminId);
  myAdminIdRef.current = myAdminId;
  const [unseen, setUnseen] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    atBottomRef.current = true;
    setUnseen(0);
  }, []);

  // A different conversation starts at its latest message.
  useLayoutEffect(() => {
    lastCountRef.current = 0;
    atBottomRef.current = true;
    setUnseen(0);
  }, [threadKey]);

  const count = visibleMessages.length;
  useLayoutEffect(() => {
    // Another search shows another subset of the same thread: not new messages.
    if (lastFilterRef.current !== filterKey) {
      const cleared = filterKey === '';
      lastFilterRef.current = filterKey;
      lastCountRef.current = count;
      setUnseen(0);
      // Leaving a search returns to the latest message, not to wherever the
      // matches were.
      if (cleared) scrollToBottom(false);
      return;
    }
    const added = count - lastCountRef.current;
    const first = lastCountRef.current === 0;
    lastCountRef.current = count;
    if (added <= 0) return;
    const visible = visibleRef.current;
    // The agent's own send: they want to see it, wherever they had scrolled.
    if (first || atBottomRef.current || isOwn(visible[count - 1], myAdminIdRef.current)) {
      scrollToBottom(!first);
      return;
    }
    // Only what the rider or the assistant wrote is news to the agent.
    const fresh = visible.slice(-added).filter(countsAsNew).length;
    if (fresh > 0) setUnseen((n) => n + fresh);
  }, [count, threadKey, filterKey, scrollToBottom]);

  // The closed banner appears at the end: bring it into view when it first
  // shows, wherever the agent had scrolled. Keyed on presence, not on the
  // element, which is a new object on every render of the page.
  const hasFooter = Boolean(footer);
  useEffect(() => {
    if (hasFooter) scrollToBottom(true);
  }, [hasFooter, scrollToBottom]);

  // A photo grows the thread when it finishes loading, after the first
  // scroll to the bottom ran: follow it if the agent was at the bottom.
  const followOnLoad = useCallback(() => {
    if (atBottomRef.current) scrollToBottom(false);
  }, [scrollToBottom]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    atBottomRef.current = atBottom;
    if (atBottom) setUnseen(0);
  };

  // The newest agent reply the rider has read, for the "Seen" mark.
  const seenId = (() => {
    if (!riderLastRead) return null;
    for (let i = visibleMessages.length - 1; i >= 0; i -= 1) {
      const m = visibleMessages[i];
      if (m.sender_type === 'admin' && !isAfter(m.created_on, riderLastRead)) return m.id;
    }
    return null;
  })();

  let lastDay = '';
  let lastSender = '';

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          bgcolor: 'background.default',
        }}
        data-testid="message-thread"
      >
        {messages === undefined ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : visibleMessages.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyText}
            </Typography>
          </Stack>
        ) : null}

        {visibleMessages.map((msg) => {
          const day = dayKey(msg.created_on);
          const newDay = day !== lastDay;
          lastDay = day;
          const senderKey = `${msg.sender_type}:${msg.sender_id}`;
          const showName = newDay || senderKey !== lastSender;
          lastSender = senderKey;

          const separator = newDay ? (
            <Divider key={`day-${day}`} sx={{ my: 1.5 }} data-testid="day-separator">
              <Typography variant="caption" color="text.secondary">
                {formatDay(msg.created_on)}
              </Typography>
            </Divider>
          ) : null;

          if (msg.sender_type === 'system') {
            return (
              <React.Fragment key={msg.id}>
                {separator}
                <Stack alignItems="center" sx={{ py: 0.5 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      <>
                        {msg.content || 'System message'} · {formatTime(msg.created_on)}
                      </>
                    }
                  />
                </Stack>
              </React.Fragment>
            );
          }

          if (msg.sender_type === 'note') {
            return (
              <React.Fragment key={msg.id}>
                {separator}
                <Box
                  sx={(theme) => ({
                    alignSelf: 'flex-end',
                    maxWidth: '75%',
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    border: 1,
                    borderColor: 'warning.main',
                    borderStyle: 'dashed',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  })}
                  data-testid="internal-note"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: 'warning.dark' }}
                  >
                    <StickyNote2OutlinedIcon sx={{ fontSize: 14 }} />
                    <Typography variant="caption" fontWeight={700}>
                      Internal note · {isOwn(msg, myAdminId) ? 'you' : msg.sender_name}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}
                  >
                    {msg.content}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <MessageTime iso={msg.created_on} />
                  </Box>
                </Box>
              </React.Fragment>
            );
          }

          const fromRider = msg.sender_type === 'user';
          const fromAssistant = msg.sender_type === 'assistant';
          const outgoing = !fromRider && !fromAssistant;
          /** A photo or video fills the bubble to its edges. */
          const visual = Boolean(msg.image_url || msg.media?.url);

          return (
            <React.Fragment key={msg.id}>
              {separator}
              <Stack
                direction="row"
                gap={1}
                justifyContent={outgoing ? 'flex-end' : 'flex-start'}
                alignItems="flex-end"
                sx={{ mt: showName ? 1 : 0 }}
              >
                {!outgoing && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 12,
                      bgcolor: fromAssistant ? 'info.main' : 'primary.main',
                      visibility: showName ? 'visible' : 'hidden',
                    }}
                  >
                    {fromAssistant ? (
                      <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      msg.sender_name?.[0]?.toUpperCase()
                    )}
                  </Avatar>
                )}
                <Box sx={{ maxWidth: '70%', minWidth: 0 }}>
                  {showName && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      justifyContent={outgoing ? 'flex-end' : 'flex-start'}
                      sx={{ mb: 0.25 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {msg.sender_name}
                      </Typography>
                      {fromAssistant && (
                        <Chip
                          size="small"
                          label="Assistant"
                          color="info"
                          variant="outlined"
                          sx={{ height: 18 }}
                        />
                      )}
                    </Stack>
                  )}
                  <Box
                    sx={{
                      bgcolor: outgoing ? 'secondary.main' : 'background.paper',
                      color: outgoing ? 'secondary.contrastText' : 'text.primary',
                      border: outgoing ? 0 : 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      borderBottomRightRadius: outgoing ? 4 : undefined,
                      borderBottomLeftRadius: outgoing ? undefined : 4,
                      px: visual ? 0.5 : 1.5,
                      py: visual ? 0.5 : 1,
                    }}
                    data-testid={
                      fromRider
                        ? 'rider-message'
                        : outgoing
                        ? 'agent-message'
                        : 'assistant-message'
                    }
                  >
                    <Attachment
                      message={msg}
                      onOpen={setLightbox}
                      onLoad={followOnLoad}
                    />
                    {msg.content && (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          px: visual ? 1 : 0,
                          pt: visual ? 0.5 : 0,
                        }}
                      >
                        {msg.content}
                      </Typography>
                    )}
                    <Box sx={{ textAlign: 'right', px: visual ? 1 : 0 }}>
                      <MessageTime iso={msg.created_on} />
                    </Box>
                  </Box>
                  {msg.actions && msg.actions.length > 0 && (
                    <MessageActionChips
                      actions={msg.actions}
                      align={outgoing ? 'flex-end' : 'flex-start'}
                    />
                  )}
                  {msg.id === seenId && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ textAlign: 'right', mt: 0.25 }}
                      data-testid="seen-receipt"
                    >
                      Seen by the rider
                    </Typography>
                  )}
                </Box>
              </Stack>
            </React.Fragment>
          );
        })}
        {footer}
      </Box>

      {unseen > 0 && (
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={<ArrowDownwardIcon />}
          onClick={() => scrollToBottom(true)}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: 4,
            boxShadow: 3,
          }}
          data-testid="new-messages-pill"
        >
          {unseen === 1 ? '1 new message' : `${unseen} new messages`}
        </Button>
      )}

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg">
        <DialogContent sx={{ p: 1 }}>
          {lightbox && (
            <Box
              component="img"
              src={lightbox}
              alt="Photo"
              sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh', mx: 'auto' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            href={lightbox ?? ''}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Open in new tab
          </Button>
          <Button onClick={() => setLightbox(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
